import JSZip from 'jszip';
import type { DetectedPlugin } from '../types';

/**
 * Bitwig Studio .bwproject parser.
 *
 * .bwproject files are ZIP archives.  Inside, Bitwig uses a binary/JSON
 * representation.  We look for any JSON-like content that contains device
 * information ("type", "name" fields typical of Bitwig device objects).
 */

function collectDevices(node: unknown, results: DetectedPlugin[]): void {
  if (!node || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    for (const item of node) collectDevices(item, results);
    return;
  }

  const obj = node as Record<string, unknown>;

  // If this node looks like a device entry
  const rawType = (obj['type'] as string | undefined)?.toLowerCase() ?? '';
  const name =
    (obj['pluginName'] as string | undefined) ||
    (obj['name'] as string | undefined);
  const vendor = obj['vendorName'] as string | undefined;

  if (
    name &&
    (rawType === 'vst3' ||
      rawType === 'vst2' ||
      rawType === 'clap' ||
      rawType === 'au')
  ) {
    const pluginType =
      rawType === 'vst3'
        ? 'VST3'
        : rawType === 'vst2'
          ? 'VST2'
          : rawType === 'clap'
            ? 'CLAP'
            : 'AU';
    results.push({
      name,
      vendor,
      pluginType,
      confidence: 0.88,
      detectionMethod: 'structured',
    });
  }

  // Recurse
  for (const value of Object.values(obj)) {
    collectDevices(value, results);
  }
}

export async function parseBitwig(
  bytes: Uint8Array
): Promise<DetectedPlugin[]> {
  const zip = await JSZip.loadAsync(bytes);
  const plugins: DetectedPlugin[] = [];

  for (const [, file] of Object.entries(zip.files)) {
    if (file.dir) continue;

    try {
      const content = await file.async('string');
      // Attempt JSON parse on files that look like JSON
      if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
        const parsed = JSON.parse(content) as unknown;
        collectDevices(parsed, plugins);
      }
    } catch {
      // Binary or non-JSON file — skip
    }
  }

  return plugins;
}
